import type { Ref } from "vue";
import { ApolloQueryResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { GetChainPaymentMethodDocument, GetChainPaymentMethodQuery } from "@/generated/graphql";
import { localStorageGetChainCode, localStorageGetCustomerCode } from "@/common/localStorageUtil";

/**
 * 支払方法と金額入力制御に必要な依存をまとめた型。
 */
interface UseDeliveryRequestRegisterPaymentParams {
  paymentRequiredFlg: Ref<boolean>;
  dummy_payment_amount: Ref<string>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
}

/**
 * 支払方法に応じた金額必須判定と、金額入力値の整形を扱う composable。
 */
export const useDeliveryRequestRegisterPayment = ({
  paymentRequiredFlg,
  dummy_payment_amount,
  errdialog,
  errMessage,
}: UseDeliveryRequestRegisterPaymentParams) => {
  const onChangePaymentMethod = async (item: string): Promise<void> => {
    try {
      const apolloClient = await generateApolloClient();

      const paymentList: ApolloQueryResult<GetChainPaymentMethodQuery> =
        await apolloClient.query<GetChainPaymentMethodQuery>({
          query: GetChainPaymentMethodDocument,
          variables: {
            chain_code: localStorageGetChainCode(),
            customer_code: localStorageGetCustomerCode(),
          },
          fetchPolicy: "no-cache",
        });
      const _paymentListData = paymentList.data.getChainPaymentMethod;
      paymentRequiredFlg.value = false;
      if (_paymentListData!.length > 0) {
        for (let i = 0; i < _paymentListData!.length; i++) {
          if (
            _paymentListData![i]!.payment_method! === item &&
            _paymentListData![i]!.payment_amount_required! === "1"
          ) {
            paymentRequiredFlg.value = true;
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        errMessage.value = "支払方法の取得に失敗しました。";
      } else {
        console.error("未知のエラー:", err);
        errMessage.value = "支払方法の取得に失敗しました。（Unknown error）";
      }
      errdialog.value = true;
      return;
    }
  };

  const commmaMoney = async (value: string): Promise<void> => {
    dummy_payment_amount.value = String(value).replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      "$1,"
    );
  };

  const removeComma = async (value: string): Promise<string> => {
    return String(value).replace(/,/g, "").replace(/¥/g, "");
  };

  const onFocus = async (value: string): Promise<void> => {
    dummy_payment_amount.value = await removeComma(value);
  };

  const onBlur = async (value: string): Promise<void> => {
    value = await removeComma(value);

    if (value) {
      const regex = new RegExp("^[0-9]+$");
      if (!regex.test(value)) {
        errdialog.value = true;
        errMessage.value = "数値およびカンマのみ入力してください";
        return;
      }
    }

    await commmaMoney(value);
  };

  return {
    onChangePaymentMethod,
    commmaMoney,
    onFocus,
    onBlur,
  };
};
